/**
 * FaceAREditor.jsx
 * 
 * Face AR placement editor with reference head
 * - Full-screen editor with 3D reference head
 * - Load product model and position it on the head
 * - Save local transform (position/rotation/scale) relative to head anchor
 * - Same coordinate system as FaceARViewer
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

// Debug logging
const DEBUG = true;
const log = (...args) => { if (DEBUG) console.log("[FaceAREditor]", ...args); };

// Product type presets
const PRODUCT_PRESETS = {
  glasses: {
    position: { x: 0, y: 0.02, z: 0.08 },
    rotation: { x: 0, y: 0, z: 0 },
    scale: { x: 0.1, y: 0.1, z: 0.1 },
    orientationCorrection: { x: 0, y: 0, z: 0 },
  },
  hat: {
    position: { x: 0, y: 0.12, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    scale: { x: 0.15, y: 0.15, z: 0.15 },
    orientationCorrection: { x: 0, y: 0, z: 0 },
  },
  mask: {
    position: { x: 0, y: -0.02, z: 0.06 },
    rotation: { x: 0, y: 0, z: 0 },
    scale: { x: 0.12, y: 0.12, z: 0.12 },
    orientationCorrection: { x: 0, y: 0, z: 0 },
  },
};

const FaceAREditor = ({
  glbUrl,
  projectName,
  projectId, // Added for preview links
  initialTransform = null,
  productType = "glasses",
  onSave,
  onCancel,
  onPreview, // Optional callback for preview
}) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Three.js refs
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const headAnchorRef = useRef(null);
  const headModelRef = useRef(null);
  const productModelRef = useRef(null);
  const productContainerRef = useRef(null);
  
  // Animation
  const animationFrameRef = useRef(null);
  
  // Transform state (in editor coordinates, same as viewer)
  const [transform, setTransform] = useState(() => {
    if (initialTransform) return { ...initialTransform };
    return { ...PRODUCT_PRESETS[productType] || PRODUCT_PRESETS.glasses };
  });
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [linkCopied, setLinkCopied] = useState(false);
  const [activeAxis, setActiveAxis] = useState("all"); // all, x, y, z
  const [transformMode, setTransformMode] = useState("position"); // position, rotation, scale
  const [showGrid, setShowGrid] = useState(true);

  // Initialize Three.js scene
  const initThreeJS = useCallback(() => {
    if (!containerRef.current) return false;
    
    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;
    
    log("Initializing editor scene:", width, "x", height);
    
    // Scene with subtle background
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a2e);
    sceneRef.current = scene;
    
    // Camera - same FOV as viewer for consistency
    const camera = new THREE.PerspectiveCamera(50, width / height, 0.01, 1000);
    camera.position.set(0, 0, 0.6);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;
    
    // Renderer
    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      antialias: true,
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    rendererRef.current = renderer;
    
    // Orbit controls for camera
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.minDistance = 0.2;
    controls.maxDistance = 2;
    controls.target.set(0, 0, 0);
    controlsRef.current = controls;
    
    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);
    
    const keyLight = new THREE.DirectionalLight(0xffffff, 0.8);
    keyLight.position.set(1, 1, 1);
    keyLight.castShadow = true;
    scene.add(keyLight);
    
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.3);
    fillLight.position.set(-1, 0.5, 0.5);
    scene.add(fillLight);
    
    const backLight = new THREE.DirectionalLight(0xffffff, 0.2);
    backLight.position.set(0, 0.5, -1);
    scene.add(backLight);
    
    // Grid helper (subtle)
    if (showGrid) {
      const gridHelper = new THREE.GridHelper(1, 10, 0x444466, 0x333355);
      gridHelper.rotation.x = Math.PI / 2;
      gridHelper.position.z = -0.2;
      scene.add(gridHelper);
    }
    
    // Head anchor - same as in viewer
    const headAnchor = new THREE.Group();
    headAnchor.name = "headAnchor";
    scene.add(headAnchor);
    headAnchorRef.current = headAnchor;
    
    log("Editor scene initialized");
    return true;
  }, [showGrid]);

  // Create reference head model - load a realistic mannequin head GLB
  const createReferenceHead = useCallback(async () => {
    if (!headAnchorRef.current) return;
    
    log("Loading reference head model...");
    
    const loader = new GLTFLoader();
    
    // Try to load a public head model, fallback to procedural if fails
    try {
      // Use a simple procedural head first, can be replaced with GLB later
      const headGroup = new THREE.Group();
      headGroup.name = "referenceHead";
      
      // Create a more realistic head shape using multiple geometries
      // Main skull - elongated sphere
      const skullGeometry = new THREE.SphereGeometry(0.095, 64, 64);
      skullGeometry.scale(0.95, 1.15, 1.0);
      const headMaterial = new THREE.MeshStandardMaterial({
        color: 0xf0e6e0,
        roughness: 0.6,
        metalness: 0.0,
      });
      const skull = new THREE.Mesh(skullGeometry, headMaterial);
      skull.position.y = 0.01;
      headGroup.add(skull);
      
      // Face front - slightly flattened area
      const faceGeometry = new THREE.SphereGeometry(0.088, 32, 32);
      faceGeometry.scale(0.95, 1.0, 0.4);
      const face = new THREE.Mesh(faceGeometry, headMaterial);
      face.position.set(0, -0.01, 0.04);
      headGroup.add(face);
      
      // Brow ridge
      const browGeometry = new THREE.BoxGeometry(0.12, 0.015, 0.03);
      browGeometry.translate(0, 0, 0);
      const browMaterial = new THREE.MeshStandardMaterial({ color: 0xebe0d8, roughness: 0.6 });
      const brow = new THREE.Mesh(browGeometry, browMaterial);
      brow.position.set(0, 0.035, 0.075);
      headGroup.add(brow);
      
      // Eye sockets (indentations)
      const eyeSocketGeometry = new THREE.SphereGeometry(0.018, 16, 16);
      const eyeSocketMaterial = new THREE.MeshStandardMaterial({ color: 0xd8ccc4, roughness: 0.7 });
      
      const leftEyeSocket = new THREE.Mesh(eyeSocketGeometry, eyeSocketMaterial);
      leftEyeSocket.position.set(-0.032, 0.02, 0.07);
      leftEyeSocket.scale.z = 0.5;
      headGroup.add(leftEyeSocket);
      
      const rightEyeSocket = new THREE.Mesh(eyeSocketGeometry, eyeSocketMaterial);
      rightEyeSocket.position.set(0.032, 0.02, 0.07);
      rightEyeSocket.scale.z = 0.5;
      headGroup.add(rightEyeSocket);
      
      // Nose bridge and tip
      const noseBridgeGeometry = new THREE.BoxGeometry(0.012, 0.04, 0.025);
      const noseMaterial = new THREE.MeshStandardMaterial({ color: 0xf0e0d8, roughness: 0.5 });
      const noseBridge = new THREE.Mesh(noseBridgeGeometry, noseMaterial);
      noseBridge.position.set(0, 0.005, 0.085);
      headGroup.add(noseBridge);
      
      const noseTipGeometry = new THREE.SphereGeometry(0.015, 16, 16);
      noseTipGeometry.scale(1, 0.8, 1);
      const noseTip = new THREE.Mesh(noseTipGeometry, noseMaterial);
      noseTip.position.set(0, -0.015, 0.095);
      headGroup.add(noseTip);
      
      // Nostrils
      const nostrilGeometry = new THREE.SphereGeometry(0.008, 8, 8);
      const nostrilMaterial = new THREE.MeshStandardMaterial({ color: 0xd8c8c0, roughness: 0.7 });
      
      const leftNostril = new THREE.Mesh(nostrilGeometry, nostrilMaterial);
      leftNostril.position.set(-0.012, -0.025, 0.085);
      headGroup.add(leftNostril);
      
      const rightNostril = new THREE.Mesh(nostrilGeometry, nostrilMaterial);
      rightNostril.position.set(0.012, -0.025, 0.085);
      headGroup.add(rightNostril);
      
      // Cheekbones
      const cheekGeometry = new THREE.SphereGeometry(0.025, 16, 16);
      cheekGeometry.scale(1.2, 0.8, 0.6);
      
      const leftCheek = new THREE.Mesh(cheekGeometry, headMaterial);
      leftCheek.position.set(-0.055, -0.01, 0.05);
      headGroup.add(leftCheek);
      
      const rightCheek = new THREE.Mesh(cheekGeometry, headMaterial);
      rightCheek.position.set(0.055, -0.01, 0.05);
      headGroup.add(rightCheek);
      
      // Jaw / chin
      const jawGeometry = new THREE.SphereGeometry(0.07, 32, 32);
      jawGeometry.scale(1.0, 0.6, 0.8);
      const jaw = new THREE.Mesh(jawGeometry, headMaterial);
      jaw.position.set(0, -0.07, 0.02);
      headGroup.add(jaw);
      
      // Chin
      const chinGeometry = new THREE.SphereGeometry(0.02, 16, 16);
      chinGeometry.scale(1.0, 0.8, 1.0);
      const chin = new THREE.Mesh(chinGeometry, headMaterial);
      chin.position.set(0, -0.09, 0.05);
      headGroup.add(chin);
      
      // Lips area
      const lipsGeometry = new THREE.TorusGeometry(0.02, 0.006, 8, 16, Math.PI);
      const lipsMaterial = new THREE.MeshStandardMaterial({ color: 0xe8c0b0, roughness: 0.4 });
      const lips = new THREE.Mesh(lipsGeometry, lipsMaterial);
      lips.rotation.x = Math.PI / 2;
      lips.rotation.z = Math.PI;
      lips.position.set(0, -0.05, 0.075);
      headGroup.add(lips);
      
      // Ears
      const earGeometry = new THREE.TorusGeometry(0.022, 0.008, 8, 16, Math.PI);
      const earMaterial = new THREE.MeshStandardMaterial({ color: 0xf0ddd5, roughness: 0.6 });
      
      const leftEar = new THREE.Mesh(earGeometry, earMaterial);
      leftEar.rotation.y = Math.PI / 2;
      leftEar.rotation.z = -Math.PI / 8;
      leftEar.position.set(-0.095, 0.01, 0);
      headGroup.add(leftEar);
      
      const rightEar = new THREE.Mesh(earGeometry, earMaterial);
      rightEar.rotation.y = -Math.PI / 2;
      rightEar.rotation.z = Math.PI / 8;
      rightEar.position.set(0.095, 0.01, 0);
      headGroup.add(rightEar);
      
      // Neck
      const neckGeometry = new THREE.CylinderGeometry(0.045, 0.055, 0.08, 32);
      const neckMaterial = new THREE.MeshStandardMaterial({ color: 0xf0e6e0, roughness: 0.6 });
      const neck = new THREE.Mesh(neckGeometry, neckMaterial);
      neck.position.set(0, -0.155, -0.01);
      headGroup.add(neck);
      
      // Shoulders / bust base
      const shouldersGeometry = new THREE.CylinderGeometry(0.12, 0.14, 0.04, 32);
      const shoulders = new THREE.Mesh(shouldersGeometry, neckMaterial);
      shoulders.position.set(0, -0.2, -0.01);
      headGroup.add(shoulders);
      
      // Add shadows
      headGroup.traverse((child) => {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      
      headAnchorRef.current.add(headGroup);
      headModelRef.current = headGroup;
      
      log("Reference head created (procedural)");
    } catch (err) {
      console.error("Failed to create reference head:", err);
    }
  }, []);

  // Load product GLB model
  const loadProductModel = useCallback(async () => {
    if (!glbUrl || !headAnchorRef.current) return false;
    
    try {
      log("Loading product model:", glbUrl);
      setLoading(true);
      setLoadProgress(0);
      
      const loader = new GLTFLoader();
      
      return new Promise((resolve, reject) => {
        loader.load(
          glbUrl,
          (gltf) => {
            const model = gltf.scene;
            
            // Create container for transforms (same structure as viewer)
            const container = new THREE.Group();
            container.name = "productContainer";
            container.add(model);
            
            // Apply current transform
            applyTransformToContainer(container, transform);
            
            // Add to head anchor
            headAnchorRef.current.add(container);
            productContainerRef.current = container;
            productModelRef.current = model;
            
            setLoading(false);
            setLoadProgress(100);
            log("Product model loaded");
            resolve(true);
          },
          (progress) => {
            if (progress.total > 0) {
              const pct = Math.round((progress.loaded / progress.total) * 100);
              setLoadProgress(pct);
            }
          },
          (error) => {
            console.error("Product model load error:", error);
            setLoading(false);
            reject(error);
          }
        );
      });
    } catch (err) {
      console.error("Product model load error:", err);
      setLoading(false);
      return false;
    }
  }, [glbUrl, transform]);

  // Apply transform to container
  const applyTransformToContainer = useCallback((container, t) => {
    if (!container) return;
    
    // Get the actual model (first child)
    const model = container.children[0];
    
    // Apply orientation correction to model itself
    if (model && t.orientationCorrection) {
      model.rotation.set(
        THREE.MathUtils.degToRad(t.orientationCorrection.x || 0),
        THREE.MathUtils.degToRad(t.orientationCorrection.y || 0),
        THREE.MathUtils.degToRad(t.orientationCorrection.z || 0)
      );
    }
    
    // Apply local transform to container
    if (t.position) {
      container.position.set(t.position.x || 0, t.position.y || 0, t.position.z || 0);
    }
    if (t.rotation) {
      container.rotation.set(
        THREE.MathUtils.degToRad(t.rotation.x || 0),
        THREE.MathUtils.degToRad(t.rotation.y || 0),
        THREE.MathUtils.degToRad(t.rotation.z || 0)
      );
    }
    if (t.scale) {
      container.scale.set(t.scale.x || 1, t.scale.y || 1, t.scale.z || 1);
    }
  }, []);

  // Update transform when state changes
  useEffect(() => {
    if (productContainerRef.current) {
      applyTransformToContainer(productContainerRef.current, transform);
    }
  }, [transform, applyTransformToContainer]);

  // Render loop
  const renderLoop = useCallback(() => {
    if (controlsRef.current) {
      controlsRef.current.update();
    }
    
    if (rendererRef.current && sceneRef.current && cameraRef.current) {
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    }
    
    animationFrameRef.current = requestAnimationFrame(renderLoop);
  }, []);

  // Initialize
  useEffect(() => {
    let mounted = true;
    
    const init = async () => {
      const threeOk = initThreeJS();
      if (!mounted || !threeOk) return;
      
      createReferenceHead();
      
      await loadProductModel();
      
      // Start render loop
      renderLoop();
    };
    
    init();
    
    return () => {
      mounted = false;
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      if (rendererRef.current) {
        rendererRef.current.dispose();
      }
      
      if (controlsRef.current) {
        controlsRef.current.dispose();
      }
    };
  }, [initThreeJS, createReferenceHead, loadProductModel, renderLoop]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current || !rendererRef.current || !cameraRef.current) return;
      
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      
      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(width, height);
    };
    
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Transform update handlers
  const updateTransform = (key, axis, value) => {
    setTransform(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [axis]: parseFloat(value) || 0,
      },
    }));
  };

  const resetToPreset = (preset) => {
    const presetData = PRODUCT_PRESETS[preset] || PRODUCT_PRESETS.glasses;
    setTransform({ ...presetData });
  };

  // Save handler
  const handleSave = () => {
    log("Saving transform:", transform);
    if (onSave) {
      onSave(transform);
    }
  };

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "#1a1a2e",
      display: "flex",
    }}>
      {/* 3D Viewport */}
      <div 
        ref={containerRef}
        style={{
          flex: 1,
          position: "relative",
        }}
      >
        <canvas ref={canvasRef} style={{ display: "block" }} />
        
        {/* Loading overlay */}
        {loading && (
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(26, 26, 46, 0.9)",
          }}>
            <div style={{ color: "#fff", textAlign: "center" }}>
              <div style={{ marginBottom: 8 }}>Ładowanie modelu...</div>
              <div style={{ fontSize: 14, opacity: 0.7 }}>{loadProgress}%</div>
            </div>
          </div>
        )}
        
        {/* Viewport title */}
        <div style={{
          position: "absolute",
          top: 16,
          left: 16,
          color: "#fff",
          fontSize: 18,
          fontWeight: "bold",
          textShadow: "0 1px 3px rgba(0,0,0,0.5)",
        }}>
          Face AR Editor: {projectName || "Model"}
        </div>
        
        {/* Instructions */}
        <div style={{
          position: "absolute",
          bottom: 16,
          left: 16,
          color: "#aaa",
          fontSize: 12,
        }}>
          Obracaj widok: przeciągnij myszą | Zoom: scroll
        </div>
      </div>
      
      {/* Control Panel */}
      <div style={{
        width: 320,
        background: "#16213e",
        padding: 20,
        overflowY: "auto",
        color: "#fff",
      }}>
        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ margin: "0 0 8px 0", fontSize: 16 }}>Ustawienia pozycji</h3>
          <p style={{ margin: 0, fontSize: 12, color: "#aaa" }}>
            Ustaw model względem głowy
          </p>
        </div>
        
        {/* Preset buttons */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, marginBottom: 8, color: "#aaa" }}>Preset:</div>
          <div style={{ display: "flex", gap: 8 }}>
            {["glasses", "hat", "mask"].map(preset => (
              <button
                key={preset}
                onClick={() => resetToPreset(preset)}
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  background: productType === preset ? "#3b82f6" : "#2a2a4a",
                  border: "none",
                  borderRadius: 6,
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: 12,
                  textTransform: "capitalize",
                }}
              >
                {preset}
              </button>
            ))}
          </div>
        </div>
        
        {/* Position controls */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: "bold", marginBottom: 12 }}>Pozycja</div>
          {["x", "y", "z"].map(axis => (
            <div key={`pos-${axis}`} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 20, fontSize: 12, color: "#aaa" }}>{axis.toUpperCase()}</span>
                <input
                  type="range"
                  min="-0.2"
                  max="0.2"
                  step="0.001"
                  value={transform.position?.[axis] || 0}
                  onChange={(e) => updateTransform("position", axis, e.target.value)}
                  style={{ flex: 1 }}
                />
                <input
                  type="number"
                  step="0.001"
                  value={transform.position?.[axis]?.toFixed(3) || "0.000"}
                  onChange={(e) => updateTransform("position", axis, e.target.value)}
                  style={{
                    width: 70,
                    padding: 4,
                    background: "#2a2a4a",
                    border: "1px solid #444",
                    borderRadius: 4,
                    color: "#fff",
                    fontSize: 11,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        
        {/* Rotation controls */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: "bold", marginBottom: 12 }}>Rotacja (°)</div>
          {["x", "y", "z"].map(axis => (
            <div key={`rot-${axis}`} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 20, fontSize: 12, color: "#aaa" }}>{axis.toUpperCase()}</span>
                <input
                  type="range"
                  min="-180"
                  max="180"
                  step="1"
                  value={transform.rotation?.[axis] || 0}
                  onChange={(e) => updateTransform("rotation", axis, e.target.value)}
                  style={{ flex: 1 }}
                />
                <input
                  type="number"
                  step="1"
                  value={transform.rotation?.[axis]?.toFixed(0) || "0"}
                  onChange={(e) => updateTransform("rotation", axis, e.target.value)}
                  style={{
                    width: 70,
                    padding: 4,
                    background: "#2a2a4a",
                    border: "1px solid #444",
                    borderRadius: 4,
                    color: "#fff",
                    fontSize: 11,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        
        {/* Scale controls */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: "bold", marginBottom: 12 }}>Skala</div>
          {["x", "y", "z"].map(axis => (
            <div key={`scale-${axis}`} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 20, fontSize: 12, color: "#aaa" }}>{axis.toUpperCase()}</span>
                <input
                  type="range"
                  min="0.01"
                  max="0.5"
                  step="0.001"
                  value={transform.scale?.[axis] || 0.1}
                  onChange={(e) => updateTransform("scale", axis, e.target.value)}
                  style={{ flex: 1 }}
                />
                <input
                  type="number"
                  step="0.001"
                  value={transform.scale?.[axis]?.toFixed(3) || "0.100"}
                  onChange={(e) => updateTransform("scale", axis, e.target.value)}
                  style={{
                    width: 70,
                    padding: 4,
                    background: "#2a2a4a",
                    border: "1px solid #444",
                    borderRadius: 4,
                    color: "#fff",
                    fontSize: 11,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        
        {/* Orientation correction */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: "bold", marginBottom: 12 }}>Korekta orientacji modelu (°)</div>
          {["x", "y", "z"].map(axis => (
            <div key={`oc-${axis}`} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 20, fontSize: 12, color: "#aaa" }}>{axis.toUpperCase()}</span>
                <input
                  type="range"
                  min="-180"
                  max="180"
                  step="1"
                  value={transform.orientationCorrection?.[axis] || 0}
                  onChange={(e) => updateTransform("orientationCorrection", axis, e.target.value)}
                  style={{ flex: 1 }}
                />
                <input
                  type="number"
                  step="1"
                  value={transform.orientationCorrection?.[axis]?.toFixed(0) || "0"}
                  onChange={(e) => updateTransform("orientationCorrection", axis, e.target.value)}
                  style={{
                    width: 70,
                    padding: 4,
                    background: "#2a2a4a",
                    border: "1px solid #444",
                    borderRadius: 4,
                    color: "#fff",
                    fontSize: 11,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        
        {/* Action buttons */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 24 }}>
          {/* Primary actions row */}
          <div style={{ display: "flex", gap: 12 }}>
            <button
              onClick={handleSave}
              style={{
                flex: 1,
                padding: "12px 16px",
                background: "#3b82f6",
                border: "none",
                borderRadius: 8,
                color: "#fff",
                cursor: "pointer",
                fontSize: 14,
                fontWeight: "bold",
              }}
            >
              Zapisz
            </button>
          </div>
          
          {/* Preview actions row */}
          {projectId && (
            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={() => {
                  const url = `${window.location.origin}/poc/face-ar/${projectId}`;
                  window.open(url, "_blank");
                }}
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  background: "#22c55e",
                  border: "none",
                  borderRadius: 8,
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                Podgląd Face AR
              </button>
              <button
                onClick={async () => {
                  const url = `${window.location.origin}/poc/face-ar/${projectId}`;
                  try {
                    await navigator.clipboard.writeText(url);
                    setLinkCopied(true);
                    setTimeout(() => setLinkCopied(false), 2000);
                  } catch (e) {
                    console.warn("Copy failed:", e);
                  }
                }}
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  background: linkCopied ? "#22c55e" : "#6366f1",
                  border: "none",
                  borderRadius: 8,
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: 13,
                  transition: "background 0.2s",
                }}
              >
                {linkCopied ? "Skopiowano!" : "Kopiuj link"}
              </button>
            </div>
          )}
          
          {/* Navigation row */}
          <div style={{ display: "flex", gap: 12 }}>
            {onCancel && (
              <button
                onClick={onCancel}
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  background: "#444",
                  border: "none",
                  borderRadius: 8,
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                Wróć do panelu
              </button>
            )}
          </div>
        </div>
        
        {/* Debug info */}
        <div style={{
          marginTop: 24,
          padding: 12,
          background: "#0a0a1a",
          borderRadius: 8,
          fontSize: 10,
          fontFamily: "monospace",
          color: "#888",
        }}>
          <div>Transform (JSON):</div>
          <pre style={{ margin: "8px 0 0 0", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
            {JSON.stringify(transform, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default FaceAREditor;
